import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";

const fmt4 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n.toFixed(4) : "";
};

const fmt2 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

export default function ProcesVerbalPrint() {
  const { id } = useParams();
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [doc, setDoc] = useState(null);
  const [gestiuneName, setGestiuneName] = useState("");
  const [contactName, setContactName] = useState("");
  const [partNames, setPartNames] = useState({});

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setBusy(true);
      setErr("");
      try {
        const d = await authFetch(`/proces-verbal/${id}`);
        if (cancelled) return;
        setDoc(d);

        // Fetch Gestiune Name
        if (d.bxgestiune_id) {
          try {
            const g = await authFetch(`/bxgestiune/${d.bxgestiune_id}`);
            if (!cancelled && g) {
              setGestiuneName(g.denumire || g.bxnume || g.name || `Gestiune #${d.bxgestiune_id}`);
            }
          } catch (e) {
            console.warn(e);
          }
        }

        // Fetch Contact Name
        if (d.contact_id) {
          try {
            const c = await authFetch(`/contacte/${d.contact_id}`);
            if (!cancelled && c) {
              setContactName(c.bxdenumire || c.name || `Contact #${d.contact_id}`);
            }
          } catch (e) {
            console.warn(e);
          }
        }

        // Fetch Part Names
        const lines = d.linii || [];
        const namesMap = {};
        for (const ln of lines) {
          if (ln.bxpart_id && !namesMap[ln.bxpart_id]) {
            try {
              const p = await authFetch(`/bxpart/${ln.bxpart_id}`);
              if (p) {
                namesMap[ln.bxpart_id] = `${p.cod || ""} ${p.denumire || ""}`.trim();
              }
            } catch (e) {
              namesMap[ln.bxpart_id] = `Part #${ln.bxpart_id}`;
            }
          }
        }
        if (!cancelled) {
          setPartNames(namesMap);
          // Print dialog
          setTimeout(() => window.print(), 300);
        }
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (busy)
    return (
      <div className="p-3">
        <Spinner animation="border" />
      </div>
    );
  if (err)
    return (
      <div className="p-3">
        <Alert variant="danger">{err}</Alert>
      </div>
    );
  if (!doc) return null;

  const linii = doc.linii || [];

  return (
    <div className="print-a4">
      {/* Header */}
      <div className="print-header">
        <div>
          <div className="print-title">PROCES VERBAL</div>
          <div className="muted mt-2">
            Nr: <b>{doc.bxdoc_no || "-"}</b> &nbsp;|&nbsp; Data:{" "}
            <b>{String(doc.bxdoc_date || "").slice(0, 10) || "-"}</b>
          </div>
        </div>
        <div className="text-end muted">
          <div>
            Gestiune: <b>{gestiuneName || `#${doc.bxgestiune_id}`}</b>
          </div>
          {doc.observatii && (
            <div className="mt-1">
              Observații: <i>{doc.observatii}</i>
            </div>
          )}
        </div>
      </div>

      {/* Partner/Supplier */}
      <div className="print-box">
        <div className="print-box-title">Partener</div>
        <div>
          <b>{contactName || `Contact #${doc.contact_id}`}</b>
        </div>
      </div>

      {/* Table lines */}
      <table className="print-table">
        <thead>
          <tr>
            <th style={{ width: "6%" }}>#</th>
            <th>Articol</th>
            <th style={{ width: "16%" }} className="text-end">
              Cantitate
            </th>
            <th style={{ width: "20%" }} className="text-end">
              Preț unitar
            </th>
            <th style={{ width: "22%" }} className="text-end">
              Valoare
            </th>
          </tr>
        </thead>
        <tbody>
          {linii.map((ln, idx) => {
            const qty = Number(ln.bxqty) || 0;
            const price = Number(ln.bxpart_regprice) || 0;
            const val = qty * price;
            return (
              <tr key={ln.id || idx}>
                <td className="muted">{idx + 1}</td>
                <td>
                  <b>{partNames[ln.bxpart_id] || ln.part_label || `Articol #${ln.bxpart_id}`}</b>
                  {ln.observatii && <div className="muted small">{ln.observatii}</div>}
                </td>
                <td className="text-end">{fmt4(ln.bxqty)}</td>
                <td className="text-end">{fmt4(ln.bxpart_regprice)}</td>
                <td className="text-end">{fmt2(val)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals */}
      <div className="print-totals">
        <div className="muted"></div>
        <div className="totals-box">
          <div className="row2 grand">
            <div>Total General</div>
            <div className="text-end">
              <b>{fmt2(doc.total)}</b>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="print-footer muted">
        <div className="grid-2">
          <div>Predat</div>
          <div>Primit / Comisie</div>
        </div>
        <div className="grid-2 mt-4">
          <div className="line" style={{ maxWidth: "200px" }}></div>
          <div className="line" style={{ maxWidth: "200px" }}></div>
        </div>
      </div>

      {/* Print Button (Screen only) */}
      <div className="no-print mt-4">
        <button className="btn btn-secondary" onClick={() => window.print()}>
          Print
        </button>
      </div>
    </div>
  );
}
